import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { moodBoardsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Plus, Trash2, Loader2, Image, X, Type, Palette, Move, ZoomIn, ZoomOut, GripVertical } from 'lucide-react';

const SWATCH_COLORS = [
  '#3b6978', '#c9a96e', '#e8dcc8', '#4a4a4a', '#8b7355', '#d4c5a9',
  '#5a7d7c', '#9c6644', '#f5f0eb', '#2c3e50', '#b8860b', '#708090',
  '#a0522d', '#556b2f', '#800020', '#deb887', '#f4e9d8', '#36454f',
];

export default function MoodBoards({ projectId }) {
  const { isAdmin, isStaff } = useAuth();
  const canEdit = isAdmin || isStaff;
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [addItemOpen, setAddItemOpen] = useState(false);
  const [activeBoard, setActiveBoard] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [boardForm, setBoardForm] = useState({ title: '', description: '' });
  const [itemType, setItemType] = useState('image');
  const [imageForm, setImageForm] = useState({ url: '', caption: '' });
  const [textForm, setTextForm] = useState({ text: '' });
  const [colorForm, setColorForm] = useState({ color: '#3b6978', caption: '' });
  const [dragging, setDragging] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const canvasRef = useRef(null);

  const fetchBoards = useCallback(async () => {
    try { const res = await moodBoardsAPI.list(projectId); setBoards(res.data); }
    catch (err) { console.error(err); } finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { fetchBoards(); }, [fetchBoards]);

  const handleCreateBoard = async (e) => {
    e.preventDefault(); setFormLoading(true);
    try { await moodBoardsAPI.create(projectId, boardForm); setCreateOpen(false); setBoardForm({ title: '', description: '' }); fetchBoards(); }
    catch (err) { console.error(err); } finally { setFormLoading(false); }
  };

  const handleAddItem = async (e) => {
    e.preventDefault(); if (!activeBoard) return; setFormLoading(true);
    const existingItems = activeBoard.images || [];
    const col = existingItems.length % 4;
    const row = Math.floor(existingItems.length / 4);
    const baseX = col * 210 + 10;
    const baseY = row * 210 + 10;

    let payload = {};
    if (itemType === 'image') {
      payload = { type: 'image', url: imageForm.url, caption: imageForm.caption, x: baseX, y: baseY, width: 200, height: 200 };
    } else if (itemType === 'text') {
      payload = { type: 'text', text: textForm.text, x: baseX, y: baseY, width: 200, height: 80 };
    } else if (itemType === 'swatch') {
      payload = { type: 'swatch', color: colorForm.color, caption: colorForm.caption, x: baseX, y: baseY, width: 100, height: 100 };
    }
    try {
      const res = await moodBoardsAPI.addImage(activeBoard.board_id, payload);
      setBoards(boards.map(b => b.board_id === activeBoard.board_id ? res.data : b));
      setActiveBoard(res.data);
      setAddItemOpen(false);
      setImageForm({ url: '', caption: '' }); setTextForm({ text: '' }); setColorForm({ color: '#3b6978', caption: '' });
    } catch (err) { console.error(err); } finally { setFormLoading(false); }
  };

  const handleDeleteItem = async (boardId, imageId) => {
    try {
      const res = await moodBoardsAPI.deleteImage(boardId, imageId);
      setBoards(boards.map(b => b.board_id === boardId ? res.data : b));
      if (activeBoard?.board_id === boardId) setActiveBoard(res.data);
      setSelectedItem(null);
    } catch (err) { console.error(err); }
  };

  const handleDeleteBoard = async (boardId) => {
    try { await moodBoardsAPI.delete(boardId); setBoards(boards.filter(b => b.board_id !== boardId)); if (activeBoard?.board_id === boardId) setActiveBoard(null); }
    catch (err) { console.error(err); }
  };

  // Drag handlers
  const onMouseDown = (e, item) => {
    if (!canEdit) return;
    e.preventDefault();
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    setDragging(item.image_id);
    setDragOffset({ x: e.clientX - rect.left - (item.x || 0), y: e.clientY - rect.top - (item.y || 0) });
    setSelectedItem(item);
  };

  const onMouseMove = (e) => {
    if (!dragging || !activeBoard || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const newX = Math.max(0, e.clientX - rect.left - dragOffset.x);
    const newY = Math.max(0, e.clientY - rect.top - dragOffset.y);
    setActiveBoard(prev => ({
      ...prev,
      images: (prev.images || []).map(img => img.image_id === dragging ? { ...img, x: newX, y: newY } : img)
    }));
  };

  const onMouseUp = async () => {
    if (!dragging || !activeBoard) { setDragging(null); return; }
    const item = (activeBoard.images || []).find(img => img.image_id === dragging);
    if (item) {
      try { await moodBoardsAPI.updateImage(activeBoard.board_id, dragging, { x: item.x, y: item.y }); }
      catch (err) { console.error(err); }
    }
    setDragging(null);
  };

  if (loading) return <div className="animate-pulse h-48 bg-muted rounded" />;

  return (
    <div className="space-y-4" data-testid="mood-boards">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">Mood Boards</h3>
        {canEdit && <Button size="sm" onClick={() => setCreateOpen(true)} data-testid="create-board-btn"><Plus className="h-4 w-4 mr-1" />New Board</Button>}
      </div>

      {boards.length === 0 ? (
        <Card><CardContent className="py-12 text-center"><Image className="h-10 w-10 mx-auto mb-3 text-muted-foreground/30" /><p className="text-muted-foreground">No mood boards yet</p><p className="text-xs text-muted-foreground mt-1">Create a board to collect inspiration images, color swatches, and notes</p></CardContent></Card>
      ) : !activeBoard ? (
        /* Board Selector */
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {boards.map(board => (
            <Card key={board.board_id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveBoard(board)} data-testid={`board-${board.board_id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h4 className="font-medium text-sm">{board.title}</h4>
                    {board.description && <p className="text-xs text-muted-foreground">{board.description}</p>}
                  </div>
                  {canEdit && (
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={(e) => { e.stopPropagation(); handleDeleteBoard(board.board_id); }}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
                {/* Thumbnail grid */}
                <div className="grid grid-cols-3 gap-1 mt-3">
                  {(board.images || []).slice(0, 6).map(img => (
                    <div key={img.image_id} className="aspect-square rounded overflow-hidden bg-muted">
                      {img.type === 'swatch' ? (
                        <div className="w-full h-full" style={{ backgroundColor: img.color }} />
                      ) : img.type === 'text' ? (
                        <div className="w-full h-full flex items-center justify-center p-1"><p className="text-[8px] text-muted-foreground line-clamp-3">{img.text}</p></div>
                      ) : (
                        <img src={img.url} alt="" className="w-full h-full object-cover" onError={e => { e.target.style.display = 'none'; }} />
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-2">{(board.images || []).length} items</p>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        /* Canvas View */
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" onClick={() => { setActiveBoard(null); setSelectedItem(null); }}>Back to Boards</Button>
              <h4 className="font-medium">{activeBoard.title}</h4>
              <span className="text-xs text-muted-foreground">{(activeBoard.images || []).length} items</span>
            </div>
            {canEdit && (
              <Button size="sm" onClick={() => setAddItemOpen(true)} data-testid="add-canvas-item-btn">
                <Plus className="h-4 w-4 mr-1" />Add Item
              </Button>
            )}
          </div>

          {/* Canvas */}
          <Card className="overflow-hidden">
            <CardContent className="p-0">
              <div
                ref={canvasRef}
                className="relative bg-[#f8f6f3] overflow-auto"
                style={{ minHeight: '500px', cursor: dragging ? 'grabbing' : 'default' }}
                onMouseMove={onMouseMove}
                onMouseUp={onMouseUp}
                onMouseLeave={onMouseUp}
                onClick={() => setSelectedItem(null)}
                data-testid="mood-board-canvas"
              >
                {(activeBoard.images || []).length === 0 ? (
                  <div className="absolute inset-0 flex items-center justify-center text-muted-foreground/40">
                    <div className="text-center"><Image className="h-12 w-12 mx-auto mb-2" /><p className="text-sm">Add images, text, or color swatches</p></div>
                  </div>
                ) : (
                  (activeBoard.images || []).map(item => {
                    const isSelected = selectedItem?.image_id === item.image_id;
                    return (
                      <div
                        key={item.image_id}
                        className={`absolute group ${isSelected ? 'ring-2 ring-primary z-20' : 'z-10 hover:ring-1 hover:ring-primary/50'}`}
                        style={{
                          left: `${item.x || 0}px`, top: `${item.y || 0}px`,
                          width: `${item.width || 200}px`, height: `${item.height || 200}px`,
                        }}
                        onMouseDown={(e) => onMouseDown(e, item)}
                        onClick={(e) => { e.stopPropagation(); setSelectedItem(item); }}
                        data-testid={`canvas-item-${item.image_id}`}
                      >
                        {/* Move handle */}
                        {canEdit && (
                          <div className="absolute -top-2 -left-2 bg-card border rounded p-0.5 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab z-30">
                            <GripVertical className="h-3 w-3 text-muted-foreground" />
                          </div>
                        )}
                        {/* Delete button */}
                        {canEdit && isSelected && (
                          <button
                            className="absolute -top-2 -right-2 bg-destructive text-white rounded-full p-0.5 z-30"
                            onClick={(e) => { e.stopPropagation(); handleDeleteItem(activeBoard.board_id, item.image_id); }}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        )}

                        {/* Content */}
                        {item.type === 'swatch' ? (
                          <div className="w-full h-full rounded-lg shadow-sm border overflow-hidden">
                            <div className="w-full flex-1" style={{ backgroundColor: item.color, height: '75%' }} />
                            <div className="bg-card p-1.5">
                              <p className="text-[10px] font-mono text-muted-foreground">{item.color}</p>
                              {item.caption && <p className="text-[10px] text-muted-foreground truncate">{item.caption}</p>}
                            </div>
                          </div>
                        ) : item.type === 'text' ? (
                          <div className="w-full h-full rounded-lg shadow-sm border bg-card p-3 overflow-hidden">
                            <p className="text-sm leading-relaxed">{item.text}</p>
                          </div>
                        ) : (
                          <div className="w-full h-full rounded-lg shadow-sm border overflow-hidden bg-card">
                            <img src={item.url} alt={item.caption || ''} className="w-full object-cover" style={{ height: item.caption ? '80%' : '100%' }} onError={e => { e.target.src = ''; e.target.className = 'w-full h-full bg-muted'; }} />
                            {item.caption && <p className="text-[10px] p-1.5 text-muted-foreground truncate">{item.caption}</p>}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Create Board Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>New Mood Board</DialogTitle><DialogDescription>Create a visual collection of inspiration</DialogDescription></DialogHeader>
          <form onSubmit={handleCreateBoard}>
            <div className="space-y-4 py-4">
              <div className="space-y-2"><Label>Title</Label><Input value={boardForm.title} onChange={e => setBoardForm({ ...boardForm, title: e.target.value })} required placeholder="e.g., Master Bedroom Palette" data-testid="board-title-input" /></div>
              <div className="space-y-2"><Label>Description</Label><Input value={boardForm.description} onChange={e => setBoardForm({ ...boardForm, description: e.target.value })} placeholder="Optional" /></div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={formLoading} data-testid="save-board-btn">{formLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Create</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Item Dialog */}
      <Dialog open={addItemOpen} onOpenChange={setAddItemOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Add to Board</DialogTitle><DialogDescription>Add an image, text note, or color swatch</DialogDescription></DialogHeader>
          <form onSubmit={handleAddItem}>
            <Tabs value={itemType} onValueChange={setItemType} className="py-4">
              <TabsList className="w-full">
                <TabsTrigger value="image" className="flex-1"><Image className="h-3.5 w-3.5 mr-1.5" />Image</TabsTrigger>
                <TabsTrigger value="text" className="flex-1"><Type className="h-3.5 w-3.5 mr-1.5" />Text</TabsTrigger>
                <TabsTrigger value="swatch" className="flex-1"><Palette className="h-3.5 w-3.5 mr-1.5" />Swatch</TabsTrigger>
              </TabsList>
              <TabsContent value="image" className="space-y-3 mt-4">
                <div className="space-y-2"><Label>Image URL</Label><Input value={imageForm.url} onChange={e => setImageForm({ ...imageForm, url: e.target.value })} required placeholder="https://..." data-testid="image-url-input" /></div>
                <div className="space-y-2"><Label>Caption</Label><Input value={imageForm.caption} onChange={e => setImageForm({ ...imageForm, caption: e.target.value })} placeholder="Optional" /></div>
                {imageForm.url && <img src={imageForm.url} alt="Preview" className="w-full h-32 object-cover rounded border" onError={e => { e.target.style.display = 'none'; }} />}
              </TabsContent>
              <TabsContent value="text" className="space-y-3 mt-4">
                <div className="space-y-2"><Label>Text Note</Label><Textarea value={textForm.text} onChange={e => setTextForm({ ...textForm, text: e.target.value })} rows={3} required placeholder="Design notes, material specs..." data-testid="text-note-input" /></div>
              </TabsContent>
              <TabsContent value="swatch" className="space-y-3 mt-4">
                <div className="space-y-2"><Label>Color</Label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={colorForm.color} onChange={e => setColorForm({ ...colorForm, color: e.target.value })} className="h-10 w-14 rounded border cursor-pointer" data-testid="swatch-color-input" />
                    <Input value={colorForm.color} onChange={e => setColorForm({ ...colorForm, color: e.target.value })} className="font-mono flex-1" />
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {SWATCH_COLORS.map(c => (
                    <button key={c} type="button" className={`w-7 h-7 rounded border-2 ${colorForm.color === c ? 'border-primary' : 'border-transparent'}`} style={{ backgroundColor: c }} onClick={() => setColorForm({ ...colorForm, color: c })} />
                  ))}
                </div>
                <div className="space-y-2"><Label>Label</Label><Input value={colorForm.caption} onChange={e => setColorForm({ ...colorForm, caption: e.target.value })} placeholder="e.g., Wall color" /></div>
                <div className="h-16 rounded-lg" style={{ backgroundColor: colorForm.color }} />
              </TabsContent>
            </Tabs>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAddItemOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={formLoading} data-testid="save-canvas-item-btn">{formLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Add to Board</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
